import { List } from 'lucide-react';
import { z } from 'zod';
import { SelectFieldInput, SelectFieldView } from '../renderers/fields/select/select-field';
import type { FieldDefinition, SelectFieldSchema } from '../types';

export const SelectDefinition: FieldDefinition<
  SelectFieldSchema,
  string | string[] | null | undefined
> = {
  type: 'select',
  toolboxLabel: 'Dropdown (Select)',
  category: 'Selection',
  description: 'A collapsible menu for a single-choice selection. Great for long lists.',
  icon: List,

  createDefault: (id) => ({
    id,
    type: 'select',
    name: `select_${id}`,
    label: 'New Select Field',
    required: false,
    placeholder: 'Select an option',
    options: ['Option 1', 'Option 2'],
  }),

  configSchema: [
    { id: 'label', type: 'text', name: 'label', label: 'Field Label', required: true },
    { id: 'name', type: 'text', name: 'name', label: 'Data Key (ID)', required: true },
    {
      id: 'placeholder',
      type: 'text',
      name: 'placeholder',
      label: 'Placeholder Text',
      required: false,
    },
    {
      id: 'required',
      type: 'checkbox',
      name: 'required',
      label: 'Required Field',
      required: false,
    },
    {
      id: 'multiple',
      type: 'checkbox',
      name: 'multiple',
      label: 'Allow Multiple Selection',
      required: false,
    },
    // Specialized list editor for options
    {
      id: 'options',
      type: 'list',
      name: 'options',
      label: 'Select Options',
      required: false,
      itemPlaceholder: 'Enter option text...',
    },
  ],

  buildValidation: (field) => {
    if (field.multiple) {
      let schema = z.array(z.string());
      if (field.required) {
        schema = schema.min(1, `${field.label} requires at least one selection`);
      }
      return schema;
    }

    const schema = z.string({ error: `${field.label} is required` });
    if (field.required) {
      return schema.min(1, `${field.label} is required`);
    }
    return schema.optional().or(z.literal(''));
  },

  editComponent: SelectFieldInput,
  viewComponent: SelectFieldView,
};
